/* ============================================================
   La Nuit — free-gift.js
   Listens for `cart:updated`, reconciles automatic free-gift line
   items against the configured tier thresholds:
     - Subtotal (excluding existing free-gift lines) ≥ threshold
       AND no matching gift line in cart  → AJAX add (qty 1) with
       properties {_gift: 'true', _tier: <threshold>}.
     - Subtotal falls below threshold AND a matching gift line exists
       → AJAX remove (change qty 0).
   A single in-flight guard prevents thrash if rapid mutations stack.
   ============================================================ */

(function () {
  const tiersEl = document.getElementById('gift-tiers');
  if (!tiersEl) return;

  let config;
  try {
    config = JSON.parse(tiersEl.textContent);
  } catch (_) { return; }
  if (!config?.tiers?.length) return;

  let busy = false;
  let pending = null;
  // Last cart signature we successfully reconciled — prevents re-running
  // the reconcile loop when cart-drawer.refresh() re-publishes the same
  // cart state we just settled on. (refresh() doesn't carry the
  // _giftReconciled flag through, so we self-dedupe by content.)
  let lastSignature = '';

  function signCart(cart) {
    if (!cart || !cart.items) return '';
    return cart.items.map(i => `${i.variant_id}:${i.quantity}:${isGiftLine(i) ? 1 : 0}`).join('|') + '|' + (cart.total_price || 0);
  }

  function isGiftLine(line) {
    return !!(line?.properties && (line.properties._gift === 'true' || line.properties._gift === true));
  }

  function subtotalExcludingGifts(cart) {
    // cart.total_price already excludes auto-discounts but includes gift line price.
    // Sum non-gift line prices instead so reaching the threshold is honest.
    return (cart.items || []).reduce((sum, line) => {
      if (isGiftLine(line)) return sum;
      return sum + (line.final_line_price ?? line.line_price ?? 0);
    }, 0);
  }

  function findGiftLine(cart, variantId) {
    return (cart.items || []).find(l => l.variant_id === variantId && isGiftLine(l));
  }

  async function addGift(variantId, threshold) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _gift: 'true', _tier: String(threshold) } }]
      })
    });
  }

  async function removeGift(lineKey) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  }

  function nonGiftItemCount(cart) {
    return (cart.items || []).reduce((n, l) => n + (isGiftLine(l) ? 0 : l.quantity), 0);
  }

  async function reconcile(cart) {
    if (!cart) return;
    const sig = signCart(cart);
    if (sig && sig === lastSignature) return; // already settled
    if (busy) { pending = cart; return; }
    busy = true;

    try {
      const eligibleSubtotal = subtotalExcludingGifts(cart);
      const realItemCount = nonGiftItemCount(cart);
      let mutated = false;

      for (const tier of config.tiers) {
        const giftLine = findGiftLine(cart, tier.variant_id);
        // Qualifies only if (a) subtotal-without-gifts meets threshold
        // AND (b) the cart has at least one real paid item — otherwise
        // we'd keep auto-adding a free gift to an otherwise-empty cart.
        const qualifies = eligibleSubtotal >= tier.threshold_cents && realItemCount > 0;

        if (qualifies && !giftLine) {
          await addGift(tier.variant_id, tier.threshold_cents);
          mutated = true;
        } else if (!qualifies && giftLine) {
          await removeGift(giftLine.key);
          mutated = true;
        }
      }

      if (mutated) {
        // Pull fresh cart and re-publish so the drawer body refreshes too.
        const fresh = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
        lastSignature = signCart(fresh);
        // Mark that this update came from free-gift so we don't recurse.
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { ...fresh, _giftReconciled: true }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } else {
        lastSignature = sig;
      }
    } catch (err) {
      console.error('Free-gift reconcile failed', err);
    } finally {
      busy = false;
      if (pending) {
        const next = pending;
        pending = null;
        reconcile(next);
      }
    }
  }

  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;
    if (!cart || cart._giftReconciled) return;
    reconcile(cart);
  });

  // Run once on load in case the page is hydrating with stale cart state.
  if (window.LaNuit?.Cart) {
    window.LaNuit.Cart.get().then(reconcile).catch(() => {});
  }
})();
